/**
 * Profile Facade (Shared)
 * 
 * Encapsulates profile page specific logic:
 * - User data management
 * - Password change
 * - Dialog state management
 * 
 * @module useProfileFacade
 * @category Facades/Shared
 */

import { useState, useCallback, useMemo } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { selectUser } from '../../../store/slices/appSlice';
import { changeUserPassword } from '../../..';
import type { ShowToastFn } from './types';

export interface ProfileFacadeReturn {
  // User data
  user: {
    name: string;
    email: string;
    provider: string;
    accessToken?: string;
  } | null;

  // Logout dialog
  logoutDialog: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };

  // Change password dialog
  changePasswordDialog: {
    isOpen: boolean;
    isChanging: boolean;
    open: () => void;
    close: () => void;
    changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  };
}

export function useProfileFacade(showToast: ShowToastFn): ProfileFacadeReturn {
  // ========== Redux State ==========
  const user = useAppSelector(selectUser);

  // ========== Local State ==========
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // ========== Logout Dialog Actions ==========
  const openLogoutDialog = useCallback(() => {
    setLogoutDialogOpen(true);
    // Analytics: auth.logout.clicked tracked via Redux middleware
  }, []);

  const closeLogoutDialog = useCallback(() => {
    setLogoutDialogOpen(false);
  }, []);

  // ========== Change Password Dialog Actions ==========
  const openChangePasswordDialog = useCallback(() => {
    setChangePasswordDialogOpen(true);
    // Analytics: profile change password tracked via data-track attribute
  }, []);

  const closeChangePasswordDialog = useCallback(() => {
    setChangePasswordDialogOpen(false);
  }, []);

  const handleChangePassword = useCallback(
    async (oldPassword: string, newPassword: string): Promise<void> => {
      setIsChangingPassword(true);
      try {
        await changeUserPassword(oldPassword, newPassword);
        
        showToast({
          message: 'Password updated successfully',
          severity: 'success',
        });
        
        setChangePasswordDialogOpen(false);
      } catch (error) {
        console.error('Change password error:', error);
        
showToast({
  message: (error as any)?.response?.data?.message || 'Failed to update password',
  severity: 'error',
});

        
        throw error; // Re-throw to let caller handle if needed
      } finally {
        setIsChangingPassword(false);
      }
    },
    [showToast]
  );

  // ========== User Data ==========
  const userData = useMemo(() => {
    if (!user) return null;
    
    return {
      name: user.name || '',
      email: user.email || '',
      provider: user.provider || '', // Use the provider field from User type
      accessToken: (user as any).accessToken,
    };
  }, [user]);

  // ========== Return ==========
  return useMemo(
    () => ({
      user: userData,
      logoutDialog: {
        isOpen: logoutDialogOpen,
        open: openLogoutDialog,
        close: closeLogoutDialog,
      },
      changePasswordDialog: {
        isOpen: changePasswordDialogOpen,
        isChanging: isChangingPassword,
        open: openChangePasswordDialog,
        close: closeChangePasswordDialog,
        changePassword: handleChangePassword,
      },
    }),
    [
      userData,
      logoutDialogOpen,
      openLogoutDialog,
      closeLogoutDialog,
      changePasswordDialogOpen,
      isChangingPassword,
      openChangePasswordDialog,
      closeChangePasswordDialog,
      handleChangePassword,
    ]
  );
}
